import { useState } from 'react'
import {
  Box, Button, Stack, Tooltip, IconButton, Chip, Menu, MenuItem,
  ListItemIcon, ListItemText, Typography, Divider, Switch,
} from '@mui/material'
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  Link as LinkIcon,
  OpenInNew as OpenIcon,
  Delete as DeleteIcon,
  TableRows as ResponsesIcon,
  // ViewIcon removed — replaced by OpenIcon inline button
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { DataTable } from '../../components/ui/DataTable'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { ConfirmDialog } from '../../components/shared/ConfirmDialog'
import { ListToolbar } from '../../components/shared/ListToolbar'
import { themeColors } from '../../app/layout/theme'
import { leadFormsApi, LeadFormSummary } from '../../lib/api/leadFormsApi'
import { EMPTY_UNIFIED_FILTERS, UnifiedFilters } from '../../components/shared/UnifiedFilterDialog'

const BASE_URL = window.location.origin

export const LeadFormsList = () => {
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const [searchQuery,   setSearchQuery]   = useState('')
  const [filters,       setFilters]       = useState<UnifiedFilters>(EMPTY_UNIFIED_FILTERS)
  const [menuAnchor,    setMenuAnchor]    = useState<{ el: HTMLElement; form: LeadFormSummary } | null>(null)
  const [deleteTarget,  setDeleteTarget]  = useState<LeadFormSummary | null>(null)

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['leadForms', searchQuery],
    queryFn:  () => leadFormsApi.getAll({ search: searchQuery, pageSize: 100 }),
    staleTime: 30_000,
  })

  const forms = data?.items ?? []

  // ── Mutations ──────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadFormsApi.delete(id),
    onSuccess: () => {
      toast.success('Form deleted')
      queryClient.invalidateQueries({ queryKey: ['leadForms'] })
      setDeleteTarget(null)
    },
    onError: () => toast.error('Failed to delete form'),
  })

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => leadFormsApi.duplicate(id),
    onSuccess: (form) => {
      toast.success(`Duplicated as "${form.name}"`)
      queryClient.invalidateQueries({ queryKey: ['leadForms'] })
    },
    onError: () => toast.error('Failed to duplicate form'),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'Active' | 'Inactive' }) =>
      leadFormsApi.toggleStatus(id, status),
    onSuccess: (_, { status }) => {
      toast.success(status === 'Active' ? 'Form activated' : 'Form deactivated')
      queryClient.invalidateQueries({ queryKey: ['leadForms'] })
    },
    onError: () => toast.error('Failed to update status'),
  })

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getFormUrl = (slug: string) => `${BASE_URL}/forms/${slug}`

  const copyUrl = (slug: string) => {
    navigator.clipboard.writeText(getFormUrl(slug))
    toast.success('Form URL copied to clipboard')
  }

  const closeMenu = () => setMenuAnchor(null)

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    {
      id: 'name',
      label: 'Form Name',
      render: (_: any, row: LeadFormSummary) => (
        <Box>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary, mt: '2px' }}>
              {row.description.length > 60 ? `${row.description.slice(0, 60)}…` : row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'org',
      label: 'Organization',
      render: (_: any, row: LeadFormSummary) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {row.orgName ?? '—'}
        </Typography>
      ),
    },
    {
      id: 'lob',
      label: 'LOB',
      render: (_: any, row: LeadFormSummary) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {row.lobName ?? '—'}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (_: any, row: LeadFormSummary) => {
        const isActive  = row.status === 'Active'
        const isPending = toggleMutation.isPending && toggleMutation.variables?.id === row.id
        return (
          <Tooltip title={isActive ? 'Click to deactivate' : 'Click to activate'}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
              onClick={(e) => {
                e.stopPropagation()
                if (!isPending) {
                  toggleMutation.mutate({ id: row.id, status: isActive ? 'Inactive' : 'Active' })
                }
              }}
            >
              <Switch
                checked={isActive}
                size="small"
                disabled={isPending}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: themeColors.success },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: themeColors.success },
                }}
              />
              <Typography sx={{
                fontSize: '12px',
                fontWeight: 600,
                color: isActive ? themeColors.success : themeColors.text.secondary,
              }}>
                {isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
    },
    {
      id: 'totalSubmissions',
      label: 'Submissions',
      render: (_: any, row: LeadFormSummary) => (
        <Chip
          label={row.totalSubmissions}
          size="small"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            bgcolor: themeColors.infoLight,
            color: themeColors.primary,
            border: `1px solid ${themeColors.primary}30`,
          }}
        />
      ),
    },
    {
      id: 'createdBy',
      label: 'Created By',
      render: (_: any, row: LeadFormSummary) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {row.createdBy?.name ?? '—'}
        </Typography>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created Date',
      render: (_: any, row: LeadFormSummary) => (
        <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
          {new Date(row.createdAt).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right' as const,
      render: (_: any, row: LeadFormSummary) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          {/* Edit */}
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => navigate(`/custom-forms/${row.id}/edit`)}
              sx={{ color: themeColors.primary, '&:hover': { bgcolor: themeColors.infoLight } }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* View Form */}
          <Tooltip title="View Form">
            <IconButton
              size="small"
              onClick={() => window.open(getFormUrl(row.slug), '_blank')}
              sx={{ color: themeColors.info, '&:hover': { bgcolor: '#e8f4fd' } }}
            >
              <OpenIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Responses */}
          <Tooltip title="View Responses">
            <IconButton
              size="small"
              onClick={() => navigate(`/custom-forms/${row.id}/responses`)}
              sx={{ color: themeColors.warning, '&:hover': { bgcolor: '#fff8e1' } }}
            >
              <ResponsesIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Copy URL */}
          <Tooltip title="Copy Form URL">
            <IconButton
              size="small"
              onClick={() => copyUrl(row.slug)}
              sx={{ color: themeColors.success, '&:hover': { bgcolor: '#e8f5e9' } }}
            >
              <LinkIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>

          {/* Overflow: Duplicate + Delete */}
          <Tooltip title="More">
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor({ el: e.currentTarget, form: row })}
              sx={{ color: themeColors.text.muted, '&:hover': { bgcolor: themeColors.background } }}
            >
              <MoreVertIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  if (isLoading) return <LoadingSpinner fullHeight label="Loading lead forms..." />

  return (
    <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, gap: 2 }}>
      <Box sx={{ flexShrink: 0 }}>
        <ListToolbar
          search={searchQuery}
          onSearch={setSearchQuery}
          searchPlaceholder="Search forms..."
          filterDefs={[]}
          filters={filters}
          onFilterChange={setFilters}
          extraActions={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                onClick={() => navigate('/custom-forms/new')}
                sx={{
                  fontSize: '13px',
                  background: themeColors.primary,
                  '&:hover': { background: themeColors.primaryDark },
                  boxShadow: '0 2px 6px rgba(75,48,130,0.3)',
                }}
              >
                New Form
              </Button>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: themeColors.border }} />
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={() => refetch()} sx={{ color: themeColors.text.secondary }}>
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataTable
          columns={columns}
          rows={forms}
          onRowClick={(row) => navigate(`/custom-forms/${row.id}/edit`)}
          emptyMessage="No lead forms yet"
          emptyHint='Click "New Form" to create your first lead capture form'
        />
      </Box>

      {/* Overflow menu — Duplicate + Delete only */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: 160, borderRadius: '8px', border: `1px solid ${themeColors.border}` },
        }}
      >
        <MenuItem onClick={() => { duplicateMutation.mutate(menuAnchor!.form.id); closeMenu() }}>
          <ListItemIcon><DuplicateIcon sx={{ fontSize: 16, color: themeColors.text.secondary }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '13px' }}>Duplicate</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => { setDeleteTarget(menuAnchor!.form); closeMenu() }}
          sx={{ color: themeColors.error }}
        >
          <ListItemIcon><DeleteIcon sx={{ fontSize: 16, color: themeColors.error }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '13px', color: themeColors.error }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Lead Form"
        description={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
            This will also remove all submissions linked to this form.
          </>
        }
        confirmLabel="Delete"
        confirmColor="error"
        isConfirming={deleteMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  )
}
